import React from 'react';

interface MelioSealProps {
  size?: number;
  className?: string;
  color?: string;
  withWordmark?: boolean;
  wordmarkSize?: number;
  ariaLabel?: string;
}

/**
 * Garden House — the Melio seal. A thin circle holds a serif "M" with a
 * small olive sprig curving along the bottom-left and breaking just past
 * the rim of the circle. Pairs with an optional Cormorant Garamond
 * wordmark to its right.
 *
 * Uses currentColor for both stroke and fill, defaulting to oxblood
 * via a wrapping span. Stays crisp at any rendered size from 16px upward.
 */
export default function MelioSeal({
  size = 32,
  className,
  color,
  withWordmark = false,
  wordmarkSize,
  ariaLabel = 'Melio',
}: MelioSealProps) {
  const computedWordmarkSize = wordmarkSize ?? Math.round(size * 0.95);
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Math.max(8, Math.round(size * 0.32)),
        color: color ?? 'var(--gh-oxblood)',
        lineHeight: 1,
      }}
      aria-label={ariaLabel}
      role="img"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="0.9" />
        <g stroke="currentColor" fill="currentColor">
          <path
            d="M 9 39 Q 21 52 32 53"
            strokeWidth="0.8"
            fill="none"
            strokeLinecap="round"
          />
          <ellipse cx="13" cy="35" rx="2.6" ry="1" transform="rotate(-72 13 35)" />
          <ellipse cx="16" cy="41" rx="2.6" ry="1" transform="rotate(-58 16 41)" />
          <ellipse cx="21" cy="46" rx="2.6" ry="1" transform="rotate(-42 21 46)" />
          <ellipse cx="27" cy="50" rx="2.6" ry="1" transform="rotate(-22 27 50)" />
          <ellipse cx="32" cy="52" rx="2.4" ry="0.95" transform="rotate(-2 32 52)" />
        </g>
        <text
          x="32"
          y="42"
          fontFamily="'Cormorant Garamond', 'Signifier', Georgia, 'Times New Roman', serif"
          fontSize="30"
          fontWeight="300"
          fill="currentColor"
          textAnchor="middle"
        >
          M
        </text>
      </svg>
      {withWordmark && (
        <span
          style={{
            fontFamily: "'Cormorant Garamond', 'Signifier', Georgia, serif",
            fontWeight: 300,
            fontSize: computedWordmarkSize,
            letterSpacing: '0.005em',
            color: 'var(--gh-graphite)',
            lineHeight: 1,
          }}
        >
          Melio
        </span>
      )}
    </span>
  );
}
